import { filter, forEach, groupBy, indexOf, isNil, keyBy, keys, map, size, some, uniq } from 'lodash';
//import { useId } from '../hooks';
import numeral from 'numeral';
import React, { ChangeEventHandler, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { Alert, Button, Form, FormGroup, Input, Label, Navbar, NavbarText, Table } from 'reactstrap';
import { gql, useMutation, useQuery } from 'urql';
import { CenteredContainer, Loading, LoadingUnitIdentifier } from '.';
import { FormProvider, SubmitHandler, useForm, UseFormRegister, UseFormSetValue, UseFormUnregister, UseFormWatch } from 'react-hook-form';
import { Adr, DangerousGoodsInput, HacpackQuery, HacpackQueryTerminalArguments, HacpackQueryUnNumbersArguments, Handling, HandlingInput, HandlingType, Terminal, TerminalHandlingsArguments } from '../maps/types/graph';
import { useHandlingTypes, useId, useMounted } from '../hooks';
import styled from '../themes/themed-styled-components';
import { DeepPartialNotListed, DeepPartialNotListedAndOverride, GraphDeepPartialNotListed, GraphDeepPartialNotListedAndOverride, GraphDeepPartialOverride, Override } from '../maps/types';
import parsePmisFile, { ParsedDangerousGoods, ParsedHandling, ParseFileOutput } from '../util/pmis-parser';
import FileDropperNew from './FileDropperNew';
import { GridLayout, HorizontalScroll } from './ui';


type PmisImporterFiles = FileList | File[];

// @ts-ignore
type PmisImportCheckerQueryData = GraphDeepPartialOverride<HacpackQuery, {
    unNumbers: PmisImportCheckerAdr[],
    terminal: PmisImportCheckerTerminal,
}>

// @ts-ignore
type PmisImportCheckerTerminal = GraphDeepPartialNotListedAndOverride<Terminal, 'id' | 'name', {
    handlings: PmisImportCheckerTerminalHandling[]
}>

// @ts-ignore
type PmisImportCheckerTerminalHandling = GraphDeepPartialNotListedAndOverride<Handling, 'id' | 'type' | 'loadingUnitIdentifier', {}>

// @ts-ignore
type PmisImportCheckerAdr = GraphDeepPartialNotListed<Adr, 'id' | 'un' | 'packingGroup' | 'hazardCls'>

type PmisImportCheckerQueryArguments = HacpackQueryUnNumbersArguments & HacpackQueryTerminalArguments & TerminalHandlingsArguments;

type PmisImportCheckerHandlingInput = Override<ParsedHandling & Pick<HandlingInput, 'id' | 'terminalName'>, {
    dangerousGoods: PmisImportCheckerDangerousGoodsInput[]
}>;

type PmisImportCheckerDangerousGoodsInput = ParsedDangerousGoods;

const pmisImportCheckerQuery = gql<PmisImportCheckerQueryData, PmisImportCheckerQueryArguments>`
query unNumbers($unNumbers: [String!]!, $id: Int!, $loadingUnitIdentifiers: [String!]) {
    unNumbers(unNumbers:$unNumbers) {
        id
        un
        packingGroup
        hazardCls
    }
    terminal(id:$id) {
        id
        name
        handlings(loadingUnitIdentifiers:$loadingUnitIdentifiers) {
            id
            type
            loadingUnitIdentifier
        }
    }
}`;

type UseImportCheckerProps = {
    terminalId: number,
    files?: PmisImporterFiles
};

type UseImportCheckerReturn = {
    checking: boolean,
    result?: ImportCheckerResult
};

type ImportCheckerResult = {
    handlings: PmisImportCheckerHandlingInput[],
    unNumbers: PmisImportCheckerAdr[]
};

const useImportChecker: ((props: UseImportCheckerProps) => UseImportCheckerReturn) = ({ terminalId: id, files }) => {
    const [checking, setChecking] = useState<boolean>(false);
    const [result, setResult] = useState<ImportCheckerResult | undefined>(undefined);
    const [variables, setVariables] = useState<PmisImportCheckerQueryArguments | undefined>(undefined);
    const [parsedData, setParsedData] = useState<ParseFileOutput[] | undefined>(undefined);
    const [handlingsByUnitNumber, setHandlingsByUnitNumber] = useState<Record<string, ParsedHandling> | undefined>(undefined);
    const mounted = useMounted();

    const [queryResult] = useQuery<PmisImportCheckerQueryData, PmisImportCheckerQueryArguments>({
        query: pmisImportCheckerQuery,
        variables,
        pause: files === undefined || variables === undefined
    });

    const { data: queryData, fetching, stale } = queryResult;

    // PARSING XML FILES
    useEffect(() => {
        setResult(undefined);

        if (files === undefined || files.length === 0) {
            return;
        }

        setChecking(true);

        let abort = false;

        Promise.all(map(files, parsePmisFile)).then((parseResult) => {
            if (abort === true || mounted.current !== true) {
                return;
            }

            setParsedData(parseResult);
        });

        return () => {
            abort = true;
            if (mounted.current !== true) {
                return;
            }

            setParsedData(undefined);
        }
    }, [files, setResult, setChecking, mounted]);

    // EXTRACT QUERY DATA
    useEffect(() => {
        const unNumbers: string[] = [];
        const parsedHandlingsByUnitNumber: Record<string, ParsedHandling> = {};

        forEach(parsedData, ({ handlings }) => {
            forEach(handlings, ({ loadingUnitIdentifier, dangerousGoods, ...rest }) => {
                const handling = parsedHandlingsByUnitNumber[loadingUnitIdentifier] = parsedHandlingsByUnitNumber[loadingUnitIdentifier] ?? { loadingUnitIdentifier, dangerousGoods: [] as ParsedDangerousGoods[], ...rest };

                forEach(dangerousGoods, ({ unNumber, ...dg }) => {
                    handling.dangerousGoods.push({ unNumber, ...dg });
                    if (indexOf(unNumbers, unNumber) === -1) {
                        unNumbers.push(unNumber);
                    }
                });
            });
        });


        if (size(parsedHandlingsByUnitNumber) === 0) {
            setResult({
                handlings: [],
                unNumbers: []
            });
            return;
        }

        setVariables({ id, checkedOut: false, loadingUnitIdentifiers: keys(parsedHandlingsByUnitNumber), unNumbers });
        setHandlingsByUnitNumber(parsedHandlingsByUnitNumber);

    }, [id, parsedData, setVariables, setHandlingsByUnitNumber, setResult]);

    // MAKE RESULT
    useEffect(() => {
        if (fetching === true || stale === true || handlingsByUnitNumber === undefined || queryData?.terminal?.handlings === undefined) {
            return;
        }

        const { unNumbers, terminal: { name: terminalName, handlings: existingHandlings } } = queryData;

        const existingHandlingsByUnitNumber = keyBy(existingHandlings, 'loadingUnitIdentifier');

        const handlings = map(handlingsByUnitNumber, ({ id, loadingUnitIdentifier, ...handling }) => ({
            ...handling,
            id: existingHandlingsByUnitNumber[loadingUnitIdentifier]?.id ?? 0,
            terminalName,
            loadingUnitIdentifier
        }));

        // @ts-ignore
        setResult({ handlings, unNumbers });

    }, [fetching, handlingsByUnitNumber, queryData, setResult, setChecking, stale]);

    useEffect(() => {
        if (result === undefined) {
            return;
        }

        setChecking(false);
    }, [result, setChecking]);

    return { result, checking };
};


const PmisImporterLoading = ({ count, onUndo }: { count:number, onUndo:()=>void }) => {
    const { t } = useTranslation('common');

    return <CenteredContainer>
        <Loading />
        <p className="mt-3">{t('pmisImporter.processingFiles', { count })}</p>
        <Button onClick={() => onUndo()}>{t('Annulla')}</Button>
    </CenteredContainer>
};

type PmisImporterDataRowProps = {
    watch: UseFormWatch<PmisImporterFormValues>,
    setValue: UseFormSetValue<PmisImporterFormValues>,
    register: UseFormRegister<PmisImporterFormValues>,
    unregister: UseFormUnregister<PmisImporterFormValues>,
    i: number,
    j: number,
    adrGroup: PmisImportCheckerAdr[],
    equipmentType: string | null,
    terminalName: string,
    handlingId: number,
    handlingUnitNumber: string,
    handlingType: HandlingType,
    handlingDangerousGoodsCount: number,
    loadingUnitType: string | null,
    loadingUnitSize: string | null,
    originLocode: string | null,
    destinationLocode: string | null,
    shippingAgencyOrCarrier: string | null,
    dangerousGoods: PmisImportCheckerDangerousGoodsInput,
    unOk: boolean
}

const PmisImporterDataRow = ({ i, j, watch, setValue, register, unregister, handlingId, handlingType, handlingUnitNumber, terminalName,
    loadingUnitType, loadingUnitSize, originLocode, destinationLocode, shippingAgencyOrCarrier, equipmentType,
    handlingDangerousGoodsCount, dangerousGoods, adrGroup, unOk }: PmisImporterDataRowProps) => {
    const { t } = useTranslation('common');
    const { id, adrClass, type, unNumber, packingGroup: pg, adrSecondaryClasses, weight, packaging, numberOfPackages, limitedQuantity, marinePollutant, elevatedTemperature, provision274, flashPoint } = dangerousGoods;
    const handlingTypes = useHandlingTypes();
    //const { register, setValue, watch } = form;

    const packingGroupField = {
        id: `handlings.${i}.dangerousGoods.${j}.packingGroup_${id}`,
        className: 'form-control',
        ...register(`handlings.${i}.dangerousGoods.${j}.packingGroup`, {
            validate: (v) => watch(`handlings.${i}.dangerousGoods.${j}.adrClass`) !== '',
            pattern: /^I{0,3}$/
        })
    };

    const [hazardClass, setHazardClass] = useState(adrClass);
    const [packingGroup, setPackingGroup] = useState(pg as '-' | '' | 'I' | 'II' | 'III');

    const availableClasses = useMemo(() => uniq(map(adrGroup, ({ hazardCls }) => hazardCls)), [adrGroup]);

    const availablePackingGroups = useMemo(() => map(filter(adrGroup, ({ hazardCls: cl }) => cl === hazardClass), ({ packingGroup }) => packingGroup), [hazardClass, adrGroup]);

    useEffect(() => () => {
        unregister(`handlings.${i}`);
    }, [i, j, unregister]);

    useEffect(() => {
        if (indexOf(availablePackingGroups, packingGroup) === -1) {
            setPackingGroup('-');
        }
    }, [availablePackingGroups, packingGroup, i, j])

    useEffect(() => {
        setPackingGroup((packingGroup) => {
            if (indexOf(availablePackingGroups, packingGroup) === -1) {
                return '-';
            }
            return packingGroup;
        });
    }, [availablePackingGroups, setPackingGroup, i, j])

    useEffect(() => {//Imposto in automatico PG vuoto se disponibile quando PG II non esiste
        setPackingGroup((packingGroup) => {
            if (unOk === true || packingGroup !== '-' || pg !== 'II' || indexOf(availablePackingGroups, 'II') !== -1 || indexOf(availablePackingGroups, '') === -1) {
                return packingGroup;
            }
            return '';
        });
    }, [availablePackingGroups, setPackingGroup, pg, unOk, i, j])

    const { onChange: onHazardClassChange, ...hazardClassField } = {
        id: `handlings.${i}.dangerousGoods.${j}.adrClass_${id}`,
        className: 'form-control',
        ...(register(`handlings.${i}.dangerousGoods.${j}.adrClass`, {
            required: true,
        }))
    };

    const handleHazardClassChange: ChangeEventHandler<HTMLSelectElement> = useCallback((ev) => {
        setPackingGroup('-');

        setHazardClass(ev.target.value);
        onHazardClassChange(ev);
    }, [onHazardClassChange, setPackingGroup, setHazardClass]);

    return <tr className={unOk ? 'table-success' : 'table-danger'}>
        {j === 0 ? <td rowSpan={handlingDangerousGoodsCount}>
            <LoadingUnitIdentifier identifier={handlingUnitNumber} />
            <div className="d-none">
                <input type="hidden" defaultValue={handlingId} {...register(`handlings.${i}.id`, { valueAsNumber: true })} />
                <input type="hidden" defaultValue={handlingType.toString()} {...register(`handlings.${i}.type`)} />
                <input type="hidden" defaultValue={terminalName} {...register(`handlings.${i}.terminalName`)} />
                <input type="hidden" defaultValue={handlingUnitNumber} {...register(`handlings.${i}.loadingUnitIdentifier`)} />
                <input type="hidden" defaultValue={loadingUnitType ?? ''} {...register(`handlings.${i}.loadingUnitType`)} />
                <input type="hidden" defaultValue={loadingUnitSize ?? ''} {...register(`handlings.${i}.loadingUnitSize`)} />
                <input type="hidden" defaultValue={equipmentType ?? ''} {...register(`handlings.${i}.equipmentType`)} />
                <input type="hidden" defaultValue={originLocode ?? ''} {...register(`handlings.${i}.originLocode`)} />
                <input type="hidden" defaultValue={destinationLocode ?? ''} {...register(`handlings.${i}.destinationLocode`)} />
                <input type="hidden" defaultValue={shippingAgencyOrCarrier ?? ''} {...register(`handlings.${i}.shippingAgencyOrCarrier`)} />
            </div>
        </td> : null}
        <td>
            {(handlingTypes as any)[type as HandlingType] ?? '-'}
            <div className="d-none">
                {<input type="hidden" defaultValue={id} {...register(`handlings.${i}.dangerousGoods.${j}.id`, { valueAsNumber: true })} />}
                {<input type="hidden" defaultValue={type?.toString()} {...register(`handlings.${i}.dangerousGoods.${j}.type`)} />}
            </div>
        </td>
        <td>{unNumber ?? '-'}
            <div className="d-none">
                {<input type="hidden" defaultValue={unNumber} {...register(`handlings.${i}.dangerousGoods.${j}.unNumber`)} />}
            </div>
        </td>

        <td>{unOk || availableClasses.length === 0
            ? <>
                {adrClass ?? '-'}<div className="d-none">
                    {<input type="hidden" defaultValue={adrClass ?? ''} {...hazardClassField} />}
                </div>
            </>
            : <select defaultValue={adrClass ?? ''} onChange={handleHazardClassChange} {...hazardClassField} style={{ width: 'auto' }}>
                <option value="">{t('--seleziona classe--')}</option>
                {availableClasses.map((cl) => <option key={cl} value={cl}>{cl}</option>)}
            </select>
        }</td>

        <td>{unOk //|| availablePackingGroups.length === 0
            ? <>
                {pg ?? '-'}<div className="d-none">
                    {<input type="hidden" defaultValue={packingGroup} {...packingGroupField} />}
                </div>
            </>
            : <select disabled={availablePackingGroups.length === 0} defaultValue={packingGroup} {...packingGroupField} style={{ width: 'auto' }}>
                <option value="-">{t('--seleziona PG--')}</option>
                {availablePackingGroups.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
        }</td>
        <td>{adrSecondaryClasses?.join(' ') ?? ''}
            <div className="d-none">
                <input type="hidden" defaultValue={adrSecondaryClasses?.join(' ')} {...register(`handlings.${i}.dangerousGoods.${j}.adrSecondaryClasses`, {
                    setValueAs: (value) => value.toString().split(' ')
                })} />
            </div>
        </td>
        <td>{packaging}
            <div className="d-none">
                <input type="hidden" defaultValue={packaging ?? ''} {...register(`handlings.${i}.dangerousGoods.${j}.packaging`)} />
            </div>
        </td>
        <td>{numberOfPackages}
            <div className="d-none">
                <input type="hidden" defaultValue={numberOfPackages ?? 0} {...register(`handlings.${i}.dangerousGoods.${j}.numberOfPackages`, {
                    valueAsNumber: true
                })} />
            </div>
        </td>
        <td>{`${numeral(weight).format('0[.]00')} kg`}
            <div className="d-none">
                <input type="hidden" defaultValue={weight ?? 0} {...register(`handlings.${i}.dangerousGoods.${j}.weight`, {
                    valueAsNumber: true
                })} />
            </div>
        </td>
        <td>{limitedQuantity === true ? <FaCheck /> : null}
            <div className="d-none">
                <input type="hidden" defaultValue={limitedQuantity ? '1' : ''} {...register(`handlings.${i}.dangerousGoods.${j}.limitedQuantity`, {
                    setValueAs: (value) => value === '1'
                })} />
            </div>
        </td>
        <td>{marinePollutant === true ? <FaCheck /> : null}
            <div className="d-none">
                <input type="hidden" defaultValue={marinePollutant ? '1' : ''} {...register(`handlings.${i}.dangerousGoods.${j}.marinePollutant`, {
                    setValueAs: (value) => value === '1'
                })} />
            </div>
        </td>
        <td>{elevatedTemperature === true ? <FaCheck /> : null}
            <div className="d-none">
                <input type="hidden" defaultValue={elevatedTemperature ? '1' : ''} {...register(`handlings.${i}.dangerousGoods.${j}.elevatedTemperature`, {
                    setValueAs: (value) => value === '1'
                })} />
                <input type="hidden" defaultValue={flashPoint ?? ''} {...register(`handlings.${i}.dangerousGoods.${j}.flashPoint`)} />
                <input type="hidden" defaultValue={provision274 ?? ''} {...register(`handlings.${i}.dangerousGoods.${j}.provision274`)} />
            </div>
        </td>
    </tr>;
};

const PmisImporterForm = styled.form`
    position: relative;
    height: 100%;
    width: 100%;
`
const PmisImporterFileDropper: typeof FileDropperNew = styled(FileDropperNew)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    margin: 0;
`

type PmisImporterMutationResponse = {
    createOrUpdateHandlings: {
        isOk: boolean
    }
}
type PmisImporterMutationVariables = {
    handlings: PmisImporterMutationHandlingInput[],
    authorize: boolean
}

type PmisImporterMutationHandlingInput = DeepPartialNotListedAndOverride<HandlingInput, 'id' | 'terminalName' | 'loadingUnitIdentifier' | 'type', {
    dangerousGoods: PmisImporterMutationDangerousGoodsInput[]
}>;

type PmisImporterMutationDangerousGoodsInput = DeepPartialNotListed<DangerousGoodsInput, 'id' | 'unNumber' | 'packingGroup' | 'type'>

const pmisImporterMutation = gql<PmisImporterMutationResponse, PmisImporterMutationVariables>`
    mutation ($handlings: [HandlingInput!]!, $authorize: Boolean) {
        createOrUpdateHandlings(handlings: $handlings, authorize: $authorize)  {
            isOk
        }
    }
`;

type PmisImporterProps = {
    id: number
}

type PmisImporterFormValues = {
    ship: string,
    arrival: string,
    handlings?: PmisImportCheckerHandlingInput[]
}

const PmisImporter = ({ id: terminalId }: PmisImporterProps) => {

    const [saveResult, save] = useMutation<PmisImporterMutationResponse, PmisImporterMutationVariables>(pmisImporterMutation);
    // @ts-ignore
    const { fetchingApproval, data } = saveResult;
    const [files, setFiles] = useState<PmisImporterFiles>();
    const [showResult, setShowResult] = useState<boolean>(false);
    const mounted = useMounted();

    useEffect(() => {
        //if (data === undefined) {
        //   return;
        //}
        setShowResult(false);
    }, [setShowResult, files]);

    const { result: checkResult, checking } = useImportChecker({ terminalId, files });

    const { id } = useId();
    const { t } = useTranslation('common');

    const form = useForm<PmisImporterFormValues>({
        defaultValues: {
            ship: '',
            arrival: '',
            handlings: []
        }
    });

    const { handleSubmit, register, setValue, watch, unregister, control } = form;

    const onSubmit: SubmitHandler<PmisImporterFormValues> = useCallback(({ ship, arrival, handlings }) => {


        if (handlings === undefined) {
            return;
        }

        const voyage = `${ship}-${arrival}`;

        save({
            handlings: handlings.map(({ ...rest }) => ({
                ...rest,
                voyage,
                ship
            })),
            authorize: true
        }).then((r) => {
            console.log({ mounted, r });
            if (mounted.current !== true) {
                return;
            }
            setShowResult(true);
        });
    }, [setShowResult, mounted, save]);

    const handlings = checkResult?.handlings;
    const unNumbersByUn = useMemo(() => groupBy(checkResult?.unNumbers, 'un'), [checkResult]);

    const shipInputField = {
        id: `ship_{id}`,
        className: 'form-control',
        ...(register('ship', {
            required: t('Questo campo è obbligatorio').toString()
        }))
    };

    const arrivalInputField = {
        id: `arrival_{id}`,
        className: 'form-control',
        ...(register('arrival', {
            required: t('Questo campo è obbligatorio').toString()
        }))
    };

    const header = useMemo(() => <Navbar className="navbar ng-white border-bottom box-shadow" style={{ padding: 0 }} light>
        <HorizontalScroll>
            <div style={{ display: 'flex', flexWrap: 'nowrap', width: 'auto', minWidth: '100%', gap: '1em' }}>
                <div style={{ flex: 0, display: 'flex', flexWrap: 'nowrap', alignItems: 'center', padding: '4px 0 4px 8px' }}>
                    <strong className="text-nowrap" style={{ lineHeight: '38px' }}>{t('Importa da PMIS')}</strong>
                </div>

                <div style={{ flex: 1, flexBasis: 'auto', alignItems: 'center', padding: '4px 0' }}>
                    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '1em' }}>
                        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
                            <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={shipInputField.id}>{t('Nave')}</Label>
                            <input type="text" disabled={fetchingApproval || showResult} {...shipInputField} />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
                            <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={arrivalInputField.id}>{t('Previsto arrivo')}</Label>
                            <input type="date" disabled={fetchingApproval || showResult} {...arrivalInputField} />
                        </div>
                    </div>
                </div>

                <div style={{ flex: 0, alignItems: 'center', padding: '4px 8px 4px 0' }}>
                    <Button type="submit" color="primary" className="form-control" disabled={fetchingApproval || showResult || files === undefined}>{t('Salva')}</Button>
                    {/*<Button className="ml-sm-2 form-control" onClick={handleCancelButtonClick} disabled={cancelDisabled}>{t('Annulla')}</Button>*/}
                </div>
            </div>
        </HorizontalScroll>
    </Navbar>, [shipInputField, arrivalInputField, fetchingApproval, showResult, files, t]);

    const tableHead = useMemo(() => <thead style={{ position: 'sticky', top: '0', background: '#fff', zIndex: 10 }}>
        <tr>
            <th>{t('Unità di carico')}</th>
            <th>{t('Tipo movimentazione')}</th>
            <th>{t('UN')}</th>
            <th>{t('Classe')}</th>
            <th><span title={t('Packing Group').toString()}>{t('PG')}</span></th>
            <th>{t('Rischio secondario')}</th>
            <th>{t('Tipo colli')}</th>
            <th>{t('Numero colli')}</th>
            <th>{t('Peso')}</th>
            <th><span title={t('Limited Quantity').toString()}>{t('LQ')}</span></th>
            <th><span title={t('Marine Pollutant').toString()}>{t('MP')}</span></th>
            <th>{t('Trasporto a caldo')}</th>
        </tr>
    </thead>, [t]);

    const formDataTable = useMemo(() => {
        if (checkResult === undefined) {
            return null;
        }

        let koCount = 0;

        const tableRows = map(handlings, ({ terminalName, id: handlingId, loadingUnitIdentifier: handlingUnitNumber, type: handlingType, equipmentType,
            loadingUnitType, loadingUnitSize, originLocode, destinationLocode, shippingAgencyOrCarrier, dangerousGoods: handlingDangerousGoods
        }, i) => <Fragment key={handlingUnitNumber}>{
            handlingType === null
                ? null//NON DOVREBBE SUCCEDERE MAI
                : map(handlingDangerousGoods, (dangerousGoods, j) => {
                    //some(adrGroup, ({ hazardCls, packingGroup: pg }) => hazardCls === adrClass && pg === packingGroup);
                    const { unNumber, adrClass, packingGroup } = dangerousGoods;

                    const adrGroup = unNumbersByUn[unNumber];
                    const unOk = some(adrGroup, ({ hazardCls: uncls, packingGroup: unpg }) => uncls === adrClass && packingGroup === unpg);

                    if (isNil(handlingType)
                        || loadingUnitType === undefined
                        || loadingUnitSize === undefined
                        || equipmentType === undefined
                        || originLocode === undefined
                        || destinationLocode === undefined
                        || shippingAgencyOrCarrier === undefined
                    ) {
                        return null;
                    }

                    if (unOk === false) {
                        koCount++;
                    }
                    
                    return <PmisImporterDataRow key={`${i}-${j}`} handlingDangerousGoodsCount={handlingDangerousGoods.length} {...{
                        i, j, dangerousGoods, watch, register, unregister, setValue, handlingId, handlingUnitNumber, terminalName,
                        loadingUnitType, loadingUnitSize, originLocode, destinationLocode, shippingAgencyOrCarrier, handlingType,
                        adrGroup, unOk, equipmentType
                    }} />
                })}</Fragment>);


        const alerMessage = koCount === 0 ? null : <div className="d-sm-inline-flex flex-sm-row">
            <NavbarText className="text-nowrap mr-sm-3">{t('pmisImporter.nonValidDangerousGoodsFound', { count: koCount })}</NavbarText>
        </div>;

        const head = <Navbar className="navbar ng-white border-bottom box-shadow" light>
            <div className="d-sm-inline-flex flex-sm-row">
                <NavbarText className="text-nowrap mr-sm-3">{t('pmisImporter.filesProcessed', { count: files?.length ?? 0 })}</NavbarText>
            </div>
            {alerMessage}
            <div className="navbar-right d-sm-inline-flex flex-sm-row">
                <Button type="button" color="light" className="ml-sm-2 form-control" disabled={fetchingApproval} onClick={() => setFiles(undefined)}>{t('Cambia file')}</Button>
            </div>
        </Navbar>;

        return <GridLayout header={head}>
            <Table className="table-sm" striped>
                {tableHead}
                <tbody>{tableRows}</tbody>
            </Table>
        </GridLayout>

    }, [t, unNumbersByUn, handlings, watch, setValue, register, unregister, checkResult, setFiles, fetchingApproval, files, tableHead]);

    const errorView = useMemo(() => saveResult?.stale === true || saveResult?.error === undefined ? null
        : <CenteredContainer>
            <Alert color="danger">{t('Si è verificato un errore')}</Alert>
            <Button onClick={() => setShowResult(false)}>{t('Riprova')}</Button>
        </CenteredContainer>, [t, saveResult, setShowResult]);

    const resultView = useMemo(() => saveResult?.stale === true ? null :
        <CenteredContainer>{saveResult?.data?.createOrUpdateHandlings?.isOk === true
            ? <>
                <Alert color="success">{t('Dati salvati')}</Alert>
                <Button onClick={() => setFiles(undefined)}>{t('Carica altri file')}</Button>
            </>
            : <>
                <Alert color="warning">{t('Si è verificato un problema')}</Alert>

                <Button onClick={() => setShowResult(false)}>{t('Riprova')}</Button>
            </>
        }</CenteredContainer>, [saveResult, setShowResult, t]);

    const content = useMemo(() => files !== undefined && files.length > 0
        ? showResult === false
            ? checking
                ? <PmisImporterLoading count={files.length} onUndo={() => setFiles(undefined)} />
                : formDataTable
            : errorView ?? resultView
        : <PmisImporterFileDropper onFilesDropped={setFiles} />, [files, setFiles, checking, formDataTable, showResult, errorView, resultView]);

    
    // @ts-ignore
    return <FormProvider {...form}>
        <PmisImporterForm id={`handlingPositionForm${id}`} onSubmit={handleSubmit(onSubmit)} noValidate>
            <GridLayout header={header}>{content}</GridLayout>
        </PmisImporterForm>
    </FormProvider>;
}

export default PmisImporter;